/**
 * Platform entegrasyonları için ortak tipler.
 *
 * Bu tanımlar önceden altı ayrı dosyada birbirinden farklı eksikliklerle
 * tekrarlanıyordu. Çoklu mağaza desteğiyle birlikte her yeni alan (storeName,
 * receiptSettingsJson) altı yeri güncellemek gerektireceği için tek kaynağa taşındı.
 */

export type MarketplacePlatform =
  | 'trendyol'
  | 'n11'
  | 'hepsiburada'
  | 'amazon'
  | 'pazarama'
  | 'pttavm';

export type FoodPlatform = 'yemeksepeti' | 'getir' | 'migros_yemek' | 'trendyol_yemek';

export type CargoPlatform = 'hepsijet' | 'mng';

export type Platform = MarketplacePlatform | FoodPlatform | CargoPlatform;

export type PlatformCategory = 'marketplace' | 'food' | 'cargo';

/** Backend PlatformIntegrationManager.StoreNameMaxLength ile aynı olmalı. */
export const STORE_NAME_MAX_LENGTH = 150;

export const FOOD_PLATFORM_KEYS: FoodPlatform[] = [
  'yemeksepeti',
  'getir',
  'migros_yemek',
  'trendyol_yemek',
];

export const PLATFORM_LABELS: Record<string, string> = {
  trendyol: 'Trendyol',
  n11: 'N11',
  hepsiburada: 'Hepsiburada',
  amazon: 'Amazon',
  pazarama: 'Pazarama',
  pttavm: 'PttAVM',
  yemeksepeti: 'Yemeksepeti',
  getir: 'Getir Food',
  migros_yemek: 'Migros Food',
  trendyol_yemek: 'Uber Eats',
  hepsijet: 'HepsiJet',
  mng: 'DHL (MNG)',
};

/**
 * Markaya özel fiş başlığı. Alan adları quickSale ReceiptSettings ile birebir
 * aynıdır; Faz 6'daki çözümleyici iki nesneyi aynı şekil altında birleştirir.
 */
export interface BrandReceiptSettings {
  headerType?: 'text' | 'logo';
  companyName?: string;
  companyAddress?: string;
  taxNumber?: string;
  logoUrl?: string;
  logoMaxWidthPx?: number;
  logoMaxHeightPx?: number;
  standardFooterNote?: string;
  thankYouMessage?: string;
}

/**
 * PlatformIntegrations tablosundaki bir kayıt.
 *
 * Aynı şirket + şube + platform için birden fazla kayıt olabilir; kayıtlar
 * birbirinden `id` ve `storeName` (mağaza/marka adı) ile ayrılır.
 */
export interface PlatformIntegration {
  id: number;
  companyId?: string;
  branchId?: string | null;
  branchName?: string | null;
  platform: Platform;
  category?: PlatformCategory;
  isEnabled: boolean;

  /**
   * Kullanıcının girdiği mağaza/marka adı. Yemek platformlarında zorunludur;
   * çoklu mağaza öncesi eklenmiş kayıtlarda boş olabilir.
   */
  storeName?: string | null;

  /**
   * Markaya özel fiş başlığı ayarlarının JSON metni. Boşsa ekran ayarındaki
   * başlık kullanılır.
   */
  receiptSettingsJson?: string | null;

  apiKey?: string | null;
  apiSecret?: string | null;
  sellerId?: string | null;
  restaurantId?: string | null;
  appSecretKey?: string | null;
  restaurantSecretKey?: string | null;

  isConnected?: boolean;
  lastSyncAt?: string | null;
  errorCount?: number;
  lastErrorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;

  /** Eski alanlar — bazı ekranlar hâlâ bu şekli okuyor. */
  enabled?: boolean;
  status?: {
    connected: boolean;
    lastSync: string;
    errorCount: number;
  };
  credentials?: {
    apiKey?: string;
    apiSecret?: string;
    sellerId?: string;
    restaurantId?: string;
    appSecretKey?: string;
    restaurantSecretKey?: string;
    lastUpdated?: string;
  };
  settings?: {
    categories?: any;
  };
}

/**
 * Mağaza adı çözümlenirken bakılan kaynak: bir entegrasyon kaydı ya da bir
 * platform siparişi olabilir. Platformlar dış mağaza kimliğini farklı adlarla
 * taşıdığı için hepsi opsiyonel tutulur.
 */
export interface StoreLabelSource {
  storeName?: string | null;
  platformIntegrationId?: number | null;
  platform?: string | null;
  /** Getir: restaurantId, Yemeksepeti: platformRestaurantId/remoteId */
  restaurantId?: string | null;
  remoteId?: string | null;
  platformRestaurantId?: string | null;
  /** Migros ve Trendyol: storeId */
  storeId?: string | number | null;
  branchId?: string | null;
}

/** 'YEMEKSEPETI', 'trendyolYemek' gibi farklı yazımları tek anahtara indirger. */
export const normalizePlatformKey = (platform?: string | null): string => {
  const raw = String(platform || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'trendyolyemek' || raw === 'trendyol-yemek' || raw === 'trendyolfood') return 'trendyol_yemek';
  if (raw === 'migros' || raw === 'migrosyemek' || raw === 'migros-yemek') return 'migros_yemek';
  if (raw === 'getirfood' || raw === 'getir_food') return 'getir';
  return raw;
};

export const isFoodPlatform = (platform?: string | null): boolean =>
  (FOOD_PLATFORM_KEYS as string[]).includes(normalizePlatformKey(platform));

const trimmed = (value?: string | null): string => (value ?? '').trim();

/**
 * Bir entegrasyon kaydının ya da siparişin hangi mağazaya (markaya) ait
 * olduğunu okunabilir bir etikete çevirir. Sırayla:
 *
 * 1. Kaydın/siparişin kendi `storeName` alanı,
 * 2. `platformIntegrationId` ile entegrasyon listesinden çözümleme,
 * 3. Platform + dış mağaza kimliği (remoteId/restaurantId/storeId) eşleşmesi,
 * 4. Şube adı.
 *
 * Hiçbiri tutmazsa boş string döner; çağıran taraf tire gösterir. Bu zincir,
 * veritabanına geriye dönük yazma yapmadan eski kayıtların da doğru markayla
 * görünmesini sağlar.
 */
export const resolveStoreLabel = (
  source: StoreLabelSource | null | undefined,
  integrations: PlatformIntegration[] = [],
  branchName?: string | null,
): string => {
  if (!source) return trimmed(branchName);

  const own = trimmed(source.storeName);
  if (own) return own;

  const match = findIntegrationForSource(source, integrations);
  const byIntegration = trimmed(match?.storeName);
  if (byIntegration) return byIntegration;

  return trimmed(branchName);
};

/**
 * Bir siparişin/kaydın hangi entegrasyona ait olduğunu bulur: önce
 * `platformIntegrationId`, sonra platform + dış mağaza kimliği eşleşmesi.
 *
 * Marka adı çözümlemesi ile marka fişi başlığı çözümlemesi aynı eşleşme
 * zincirine dayandığı için tek yerde tutuluyor.
 */
export const findIntegrationForSource = (
  source: StoreLabelSource | null | undefined,
  integrations: PlatformIntegration[] = [],
): PlatformIntegration | undefined => {
  if (!source) return undefined;

  if (source.platformIntegrationId) {
    const byId = integrations.find((i) => i.id === source.platformIntegrationId);
    if (byId) return byId;
  }

  const platformKey = normalizePlatformKey(source.platform);
  if (!platformKey) return undefined;

  // Dış mağaza kimliği entegrasyonda her platform için RestaurantId alanında tutulur.
  const externalIds = [source.remoteId, source.platformRestaurantId, source.restaurantId, source.storeId]
    .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
    .filter((v) => v.length > 0 && v !== '0');

  if (externalIds.length === 0) return undefined;

  return integrations.find(
    (i) =>
      normalizePlatformKey(i.platform) === platformKey &&
      externalIds.includes(trimmed(i.restaurantId)),
  );
};
